/**
 * Actor - base class for any object that can be placed in a Genesys world.
 * Wraps a root SceneComponent to anchor visuals, physics, and child components.
 * Handles lifecycle events (begin/end play, tick) and supports serialization via WorldSerializer.
 */

import 'reflect-metadata';
import * as THREE from 'three';

import { PrimitiveComponent } from '../components/PrimitiveComponent.js';
import { ObjectFlags, SceneComponent } from '../components/SceneComponent.js';
import { FeatureFlags } from '../const.js';
import { Playable, PlayState } from '../game/Playable.js';
import { getDeviceId, type INetObject } from '../multiplayer/NetIdentity.js';
import { AbstractConstructor, ClassRegistry, EngineClass } from '../systems/ClassRegistry.js';
import { ObjectNamingSystem } from '../systems/ObjectNamingSystem.js';
import { Delegate } from '../utils/Delegate.js';
import { DescriptionHelper } from '../utils/DescriptionHelpers.js';
import { isAsyncLoadable } from '../utils/IAsyncLoadable.js';
import { objectInitOptionsTracker } from '../utils/ObjectInitOptionsTracker.js';
import { property } from '../utils/serialization/decorator.js';
import { createDumper } from '../utils/serialization/factories.js';
import { ISerializableObjectTag } from '../utils/serialization/serializable.js';
import { DumperFlags, PrefabInfo, SerializedData } from '../utils/serialization/types.js';
import { ensure, type ExcludeFunctionFields } from '../utils/Utils.js';
import { shortUUID } from '../utils/uuid.js';

import {
  ActorNetTransform,
  type IRPCTarget,
  NetRole,
  RPC_METADATA_KEY,
} from './ActorNetworking.js';

import type { RPCMethodMetadata } from './ActorNetworking.js';
import type { PlayerController } from './PlayerController.js';
import type { DamageHitInfo } from '../components/CharacterStatsComponent.js';
import type { World } from '../game/World.js';
import type { CollisionEvent, IPhysicsEngine } from '../physics/PhysicsEngine.js';
import type { ComponentPhysicsOptions } from '../physics/PhysicsTypes.js';
import type { EditorClassMeta, EditorPropertyChangedResult, IEditorObject } from '../utils/EditorClassMeta.js';
import type { IAsyncLoadable } from '../utils/IAsyncLoadable.js';
import type { ISceneGraphDebugNodeGenerator, SceneGraphDebugNode } from '../utils/SceneGraphUtils.js';
import type { IDumper, ILoader } from '../utils/serialization/factories.js';


export type ActorTickDelegate = Delegate<[Actor, number], void>;
export type ActorAddedToWorldDelegate = Delegate<[Actor, World], void>;
export type GenericActorDelegate = Delegate<[Actor], void>;
export type ActorClickDelegate = Delegate<[Actor, THREE.Intersection], void>;
export type ActorCollisionDelegate = Delegate<[Actor, SceneComponent, Actor, SceneComponent, CollisionEvent], void>;

/** Options for configuring an Actor instance */
export interface ActorOptions {
  /** Initial position in world space */
  position?: THREE.Vector3;
  /** Initial rotation in world space */
  rotation?: THREE.Euler;
  /** Initial scale in world space */
  scale?: THREE.Vector3;
  /** Initial root component */
  rootComponent?: SceneComponent;
  /** Initial scene components added under the root component */
  sceneComponents?: SceneComponent[];
  /** Tags for identifying and grouping actors */
  actorTags?: string[];
  /** Initial name of the actor */
  name?: string;
}

/** Options for describing an actor in the scene */
export interface ActorDescriptionOptions {
  /** Whether to include the actor's components in the description */
  includeComponentsDetails?: boolean;
  /** If true, will omit all description and only include the name and uuid */
  omitDescription?: boolean;
}

type InitOptions<T> = T extends { initialize(options: infer O): any } ? ExcludeFunctionFields<O> : never;
@EngineClass('Actor')
export class Actor extends Playable implements
  ISceneGraphDebugNodeGenerator,
  IEditorObject,
  INetObject,
  IRPCTarget {
  static readonly EDITOR_CLASS_META: EditorClassMeta = {} as const;

  readonly [ISerializableObjectTag] = true;

  @property({editorOnly: true, hidden: true})
  public editorData: Record<string, any> = {};

  @property({transientOnPrefab: true, readonly: true, order: -20})
  readonly uuid: string = shortUUID();

  @property({order: -19, hidden: true})
  public name: string;
  private _rootComponent: SceneComponent;
  protected world: World | null = null;
  protected lifeSpan: number = 0;
  protected remoteNetClientId: string | null = null;
  protected remoteNetDeviceId: string | null = null;

  /**
   * Enable simple snap-to-position transform replication for this actor.
   * When true, position/rotation/scale are replicated via {@link netTransform} and applied
   * directly on clients each tick. Intended for non-character actors (projectiles, items, etc.).
   * Pawns use {@link PawnNetTransform} instead.
   */
  @property({ category: 'Multiplayer' })
  public replicateTransform: boolean = false;

  /**
   * Replicated transform state. Only populated and sent when {@link replicateTransform} is true.
   * Must be declared after {@link replicateTransform} so the deserializer sets the flag first.
   */
  @property({ replicate: true, transient: true, hidden: true })
  protected netTransform: ActorNetTransform = new ActorNetTransform();

  /**
   * Whether this actor is replicated over the network
   * - false: No replication (Tier 1 - static non-replicated objects)
   * - true: Replicated according to netLocalRole and netRemoteRole
   */
  private _replicated: boolean = false;

  /** Lazily-built cache of replicated components indexed by sub-ID (sorted by uuid). */
  private _replicatedComponentMap: SceneComponent[] | null = null;

  /**
   * Local network role - what is this actor's role on THIS machine?
   * Determines if this actor should simulate, send updates, or just receive
   * Only used when replicated = true
   */
  private _netLocalRole: NetRole = NetRole.Authority;

  /**
   * Remote network role - what is this actor's role on the REMOTE machine?
   * Used to determine replication behavior from the perspective of other machines
   * Only used when replicated = true
   *
   * NOTE: This is set automatically based on the 'replicated' property:
   * - replicated = true -> netRemoteRole = SimulatedProxy
   * - replicated = false -> netRemoteRole = None
   * Use setNetRemoteRoleAutonomousProxy() to set AutonomousProxy for player-controlled actors
   */
  private _netRemoteRole: NetRole = NetRole.None;

  /**
   * Which client owns this actor (for player-controlled actors, Tier 4 only)
   * Null for server-owned actors
   */
  private _netOwningClientId: string | null = null;

  /**
   * If true, this actor is only relevant to its owning client (netOwningClientId).
   * When true, skips all other relevancy checks and only replicates to the owner.
   * Default: false (replicate to all clients based on normal relevancy rules)
   *
   * Use cases:
   * - PlayerController (only the owning client needs to know about it)
   * - Player-specific UI actors
   * - Client-specific game state
   */
  private _onlyRelevantToOwner: boolean = false;

  // deprecate in favor of $$prefab
  protected prefabName: string | null = null;
  protected prefabData: SerializedData | null = null;
  protected prefabInheritanceChain: Record<string, SerializedData> | null = null;

  // make it protected/private later
  public $prefab: PrefabInfo | null = null;

  private transient: boolean = false;
  protected declare readonly create: never;

  @property({
    type: 'multiselect',
    options: [],
    createNewAllowed: true,
    placeholder: 'Add tags...',
    useDisplayCase: false,
    description: 'Actor tags for categorization and filtering',
    order: -18
  })
  protected actorTags: string[] = [];

  @property({category: 'Advanced', categoryOrder: 100})
  private hiddenInGame: boolean = false;
  @property({category: 'Advanced'})
  private editorOnly: boolean = false;

  /** Invoked when this actor is added to a world */
  public readonly onAddedToWorld: ActorAddedToWorldDelegate = new Delegate<[Actor, World], void>();
  /** Invoked during tick before physics simulation */
  public readonly onTickPrePhysics: ActorTickDelegate = new Delegate<[Actor, number], void>();
  /** Invoked during tick after physics simulation */
  public readonly onTickPostPhysics: ActorTickDelegate = new Delegate<[Actor, number], void>();
  /** Invoked when this actor begins play */
  public readonly onBeginPlay: GenericActorDelegate = new Delegate<[Actor], void>();
  /** Invoked when this actor ends play */
  public readonly onEndPlay: GenericActorDelegate = new Delegate<[Actor], void>();
  /** Invoked when this actor is clicked */
  public readonly onClicked: ActorClickDelegate = new Delegate<[Actor, THREE.Intersection], void>();

  /**
   * Invoked when this actor collides with another actor
   * Parameters: [thisActor, thisActorComponent, otherActor, otherActorComponent, collisionEvent]
   */
  public readonly onCollideWith: ActorCollisionDelegate = new Delegate<[Actor, SceneComponent, Actor, SceneComponent, CollisionEvent], void>();
  /**
   * Invoked when this actor overlaps with another actor
   * Parameters: [thisActor, thisActorComponent, otherActor, otherActorComponent, collisionEvent]
   */
  public readonly onOverlapWith: ActorCollisionDelegate = new Delegate<[Actor, SceneComponent, Actor, SceneComponent, CollisionEvent], void>();
  /**
   * Invoked when this actor stops colliding with another actor
   * Parameters: [thisActor, thisActorComponent, otherActor, otherActorComponent, collisionEvent]
   */
  public readonly onStopCollidingWith: ActorCollisionDelegate = new Delegate<[Actor, SceneComponent, Actor, SceneComponent, CollisionEvent], void>();
  /**
   * Invoked when this actor stops overlapping with another actor
   * Parameters: [thisActor, thisActorComponent, otherActor, otherActorComponent, collisionEvent]
   */
  public readonly onStopOverlappingWith: ActorCollisionDelegate = new Delegate<[Actor, SceneComponent, Actor, SceneComponent, CollisionEvent], void>();

  constructor() {
    super();
    this.name = ObjectNamingSystem.generateName(this);
    this._rootComponent = SceneComponent.create();
    this.rootComponent._privateSetActor(this);
  }

  public setName(name: string): void {
    this.name = name;
  }

  public static readonly create = function <T extends Actor>(
    this: new () => T,
    ...args: T extends { initialize(): any }
      ? [options?: InitOptions<T>]
      : [options: InitOptions<T>]
  ): T {
    const actor = new this();
    const [options] = args;
    actor.initialize(options);
    if (FeatureFlags.PERSIST_INIT_OPTIONS) {
      /*
      * nullify all the options fields that are already serialized as part of the actor state
      * no need to store them separately
      * note that we cannot nullify rootComponent/sceneComponents, otherwise the case below will fail
      * const actor = Actor.create({
      *   rootComponent: SceneComponent.create(),
      *   sceneComponents: [SceneComponent.create()],
      * })
      * const loadedActor = load(save(actor))
      * the loaded actor would be created with empty init options
      * and the serialized components will be loaded as "new" children with the DefaultSubObject flag
      * causing them to be incorrectly discarded in SceneComponent.deserialize()
      */
      objectInitOptionsTracker.record(actor, {
        ...options,
        position: undefined,
        rotation: undefined,
        scale: undefined,
        name: undefined,
        actorTags: undefined,
      });
    }
    return actor;
  };

  /**
   * Initializes the actor with the given options. Called by Actor.create method.
   * Note: This method is only used when an actor is created by create method, and won't be called when an actor is deserialized from save file.
   * @param options - The options to initialize the actor with
   */
  public initialize(options?: ActorOptions): void {
    this.actorTags.push(...options?.actorTags ?? []);

    this.name = options?.name ?? this.name;

    let rootComponent = options?.rootComponent;
    if (rootComponent && !ensure(rootComponent.getActor() === null)) {
      rootComponent = undefined;
    }

    if (rootComponent) {
      this.setRootComponent(rootComponent, false);
    }

    if (options?.position) {
      this.rootComponent.position.copy(options?.position);
    }
    if (options?.rotation) {
      this.rootComponent.rotation.copy(options?.rotation);
    }
    if (options?.scale) {
      this.rootComponent.scale.copy(options?.scale);
    }

    for (const component of options?.sceneComponents ?? []) {
      if (component instanceof SceneComponent) {
        this.rootComponent.add(component);
      } else {
        console.trace(`${component} is not a SceneComponent`);
      }
    }

    this.rootComponent.updateMatrixWorld(true);
    this._netRemoteRole = this._replicated ? NetRole.SimulatedProxy : NetRole.None;

    for (const object of this.getComponents(THREE.Object3D)) {
      object.setFlags(ObjectFlags.DefaultSubObject);
    }
  }

  /**
   * Called after the actor is loaded from a save file. Not called when created by create method.
   * Used for extra initialization that depends on serialized data.
   */
  public postLoad(): void {
    // Hook for subclasses to perform post-load initialization
  }

  public getPrefabName(): string | null {
    return this.prefabName ?? this.$prefab?.$path ?? null;
  }

  public getPrefabData(): SerializedData | null {
    return this.prefabData;
  }

  public getPrefabInheritanceChain(): Record<string, SerializedData> | null {
    return this.prefabInheritanceChain;
  }

  /**
   * @deprecated Use $$prefab, setAsPrefabInstance instead
   * @param name - The prefab name
   * @param data - The prefab data
   * @param inheritanceChain - The full prefab inheritance chain (optional)
   * @returns This actor instance for method chaining
   */
  public setPrefab(name: string, data: SerializedData, inheritanceChain?: Record<string, SerializedData>): this {
    this.prefabName = name;
    this.prefabData = data;
    this.prefabInheritanceChain = inheritanceChain ?? null;
    return this;
  }

  /**
   * @deprecated Use setAsPrefabInstance(null) instead
   * @returns This actor instance for method chaining
   */
  public clearPrefab(): this {
    this.prefabName = null;
    this.prefabData = null;
    this.prefabInheritanceChain = null;
    return this;
  }

  public getActorTags(): string[] {
    return [...this.actorTags];
  }

  public setActorTags(tags: string[]): this {
    this.actorTags = [...tags];
    return this;
  }

  public addActorTag(tag: string): this {
    if (!this.actorTags.includes(tag)) {
      this.actorTags.push(tag);
    }
    return this;
  }

  public removeActorTag(tag: string): this {
    const index = this.actorTags.indexOf(tag);
    if (index !== -1) {
      this.actorTags.splice(index, 1);
    }
    return this;
  }

  public hasActorTag(tag: string): boolean {
    return this.actorTags.includes(tag);
  }

  public hasAnyActorTag(tags: string[]): boolean {
    return tags.some(tag => this.actorTags.includes(tag));
  }

  public hasAllActorTags(tags: string[]): boolean {
    return tags.every(tag => this.actorTags.includes(tag));
  }

  public setTransient(transient: boolean): void {
    this.transient = transient;
  }

  public isTransient(): boolean {
    return this.transient;
  }

  /**
   * Triggers the click event on this actor
   * @param intersection - The intersection data from raycasting
   */
  public clicked(intersection: THREE.Intersection): void {
    this.onClicked.invoke(this, intersection);
  }

  public get rootComponent(): SceneComponent {
    return this._rootComponent;
  }

  /**
   * Sets the root scene component of this actor
   * @param newRootComponent - The new root scene component
   * @param copyTransform - Whether to copy the transform from the existing root component
   */
  public setRootComponent(newRootComponent: SceneComponent, copyTransform: boolean): this {
    if (!ensure(!this.isPlayEnded())) {
      return this;
    }

    if (!ensure(newRootComponent.getActor() === null)) {
      return this;
    }

    if (copyTransform) {
      newRootComponent.position.copy(this.rootComponent.position);
      newRootComponent.rotation.copy(this.rootComponent.rotation);
      newRootComponent.scale.copy(this.rootComponent.scale);
    }

    const oldRootComponent = this.rootComponent;
    oldRootComponent.removeFromParent();
    oldRootComponent._privateSetActor(null);
    this._rootComponent = newRootComponent;
    this.rootComponent._privateSetActor(this);
    this.rootComponent.updateMatrixWorld(true);
    if (this.isPlaying()) {
      this.rootComponent.beginPlay();
    }
    if (this.world) {
      this.world.scene.add(newRootComponent);
    }
    return this;
  }

  /**
   * If the root component is a SceneComponent and has a single child, replace the root with the child.
   * @returns True if the root component was collapsed
   */
  public collapseRootComponent(): boolean {
    if (ensure(this.getPlayState() === PlayState.NotStarted)) {
      // don't use "instanceof SceneComponent" as we want pure equality check
      if (ClassRegistry.getClassOrConstructorName(this.rootComponent) === 'SceneComponent' && this.rootComponent.children.length === 1) {
        const child = this.rootComponent.children[0];
        if (ensure(child instanceof SceneComponent)) {
          child.removeFromParent();
          this.setRootComponent(child as SceneComponent, true);
          return true;
        }
      }
    }
    return false;
  }

  public getWorld(): World | null {
    return this.world;
  }

  /**
   * Updates this actor before physics simulation
   * @param deltaTime - Time in seconds since the last tick
   */
  public tickPrePhysics(deltaTime: number): void {
    if (this.lifeSpan > 0) {
      this.lifeSpan -= deltaTime;
      if (this.lifeSpan <= 0) {
        this.destroy();
        return;
      }
    }

    this.rootComponent.tickPrePhysics(deltaTime);
    this.onTickPrePhysics.invoke(this, deltaTime);
  }

  /**
   * Updates this actor after physics simulation
   * @param deltaTime - Time in seconds since the last tick
   */
  public tickPostPhysics(deltaTime: number): void {
    this.rootComponent.tickPostPhysics(deltaTime);
    this.onTickPostPhysics.invoke(this, deltaTime);
    if (this.replicateTransform && !this.isSimulatedProxy()) {
      this.netTransform.position.copy(this.rootComponent.getWorldPosition());
      this.netTransform.rotation.copy(this.rootComponent.getWorldRotation());
      this.netTransform.scale.copy(this.rootComponent.getWorldScale());
    }
  }

  public tickInEditor(deltaTime: number): void {
    this.rootComponent.tickInEditor(deltaTime);
  }

  public onRepNetTransform(): void {
    if (!this.replicateTransform) return;
    if (this.isSimulatedProxy() && this.isPhysicsEnabled()) {
      this.setPhysicsEnabled(false);
    }
    this.rootComponent.position.copy(this.netTransform.position);
    this.rootComponent.rotation.copy(this.netTransform.rotation);
    this.rootComponent.scale.copy(this.netTransform.scale);
  }

  public forwardDirection(): THREE.Vector3 {
    return new THREE.Vector3(0, 0, -1).applyQuaternion(this.rootComponent.quaternion).normalize();
  }

  public rightDirection(): THREE.Vector3 {
    return new THREE.Vector3(1, 0, 0).applyQuaternion(this.rootComponent.quaternion).normalize();
  }

  public upDirection(): THREE.Vector3 {
    return new THREE.Vector3(0, 1, 0).applyQuaternion(this.rootComponent.quaternion).normalize();
  }

  /**
   * Returns the session physics engine via the owning game loop.
   * TODO(editor): Editor worlds will resolve physics through a future {@link EditorGameLoop}.
   */
  public getPhysicsEngine(): IPhysicsEngine | null {
    const world = this.world;
    if (!world) {
      return null;
    }

    return world.gameLoop?.physicsEngine ?? null;
  }

  public setHidden(hidden: boolean): this {
    if (this.isHidden() !== hidden) {
      this.rootComponent.setHidden(hidden, true);
    }
    return this;
  }

  public isHidden(): boolean {
    return this.rootComponent.isHidden();
  }

  public replacePhysicsOptions(newOptions: ComponentPhysicsOptions, propagateToChildren: boolean = false): this {
    this.rootComponent.replacePhysicsOptions(newOptions, propagateToChildren);
    return this;
  }

  public overridePhysicsOptions(newOptions: ComponentPhysicsOptions, propagateToChildren: boolean = false): this {
    this.rootComponent.overridePhysicsOptions(newOptions, propagateToChildren);
    return this;
  }

  public setPhysicsEnabled(enable: boolean, propagateToChildren: boolean = false): this {
    this.rootComponent.setPhysicsEnabled(enable, propagateToChildren);
    return this;
  }

  public isPhysicsEnabled(): boolean {
    return (this.rootComponent instanceof PrimitiveComponent) && this.rootComponent.isPhysicsEnabled();
  }

  public generateDebugNode(): SceneGraphDebugNode | null {
    const children: (SceneGraphDebugNode | null)[] = [];
    children.push(this.rootComponent.generateDebugNode());
    return {
      title: this.name,
      children,
      data: {
        class: ClassRegistry.getClassOrConstructorName(this),
        uuid: this.uuid,
      }
    };
  }

  public getLifeSpan(): number {
    return this.lifeSpan;
  }

  public setLifeSpan(lifeSpan: number): this {
    this.lifeSpan = lifeSpan;
    return this;
  }

  public addComponent(component: SceneComponent): this {
    return this.addComponents(component);
  }

  public addComponents(...components: SceneComponent[]): this {
    components.forEach(component => {
      this.rootComponent.add(component);
    });
    return this;
  }

  public getComponent<T extends THREE.Object3D>(type: AbstractConstructor<T> = SceneComponent as any): T | null {
    return this.rootComponent.getComponent(type);
  }

  public getComponents<T extends THREE.Object3D>(type: AbstractConstructor<T> = SceneComponent as any): T[] {
    return this.rootComponent.getComponents(type);
  }

  public getPathName(): string {
    return this.world ? `${this.world.getPathName()}.${this.name}` : this.name;
  }

  public destroy(): void {
    if (this.world) {
      this.world.removeActors(this);
    }
  }

  /**
   * Handles the death of this actor
   * @param hitInfo - The hit information
   */
  public handleDeath(hitInfo?: DamageHitInfo): void {
    this.destroy();
  }

  public asExportedObject(): any {
    const dumper = createDumper({ flags: DumperFlags.None });
    return dumper.dump(this);
  }

  public asExportedPrefabData(): any {
    const dumper = createDumper({ flags: DumperFlags.AsPrefab });
    return dumper.dump(this);
  }

  /**
   * Clears root local transform values for prefab-template export workflows.
   */
  public stripRootLocalTransform(): void {
    const root = this.rootComponent;
    if (!root) {
      return;
    }

    root.position.set(0, 0, 0);
    root.rotation.set(0, 0, 0);
    root.quaternion.set(0, 0, 0, 1);
    root.scale.set(1, 1, 1);
  }

  public setWorldTransform(transform: { position?: THREE.Vector3, rotation?: THREE.Euler, scale?: THREE.Vector3 }): void {
    this.rootComponent.setWorldTransform(transform);
  }

  public setWorldPosition(position: THREE.Vector3): void {
    this.setWorldTransform({ position });
  }

  public setWorldRotation(rotation: THREE.Euler): void {
    this.setWorldTransform({ rotation });
  }

  public setWorldScale(scale: THREE.Vector3): void {
    this.setWorldTransform({ scale });
  }

  public setWorldQuaternion(quaternion: THREE.Quaternion): void {
    this.setWorldTransform({ rotation: new THREE.Euler().setFromQuaternion(quaternion) });
  }

  public getWorldPosition(): THREE.Vector3 {
    return this.rootComponent.getWorldPosition();
  }

  public getWorldRotation(): THREE.Euler {
    return this.rootComponent.getWorldRotation();
  }

  public getWorldScale(): THREE.Vector3 {
    return this.rootComponent.getWorldScale();
  }

  public getWorldQuaternion(): THREE.Quaternion {
    return this.rootComponent.getWorldQuaternion();
  }

  public getWorldTransform(): { position: THREE.Vector3, rotation: THREE.Euler, scale: THREE.Vector3 } {
    return this.rootComponent.getWorldTransform();
  }



  /**
   * Determines if this actor is relevant for network replication to a specific client.
   * Override to implement per-client relevance filtering (e.g., distance-based culling).
   * @param _clientId - Optional client ID to check relevance for
   * @param _clientData - Optional client data for relevance checks
   * @returns True if this actor should be replicated
   */
  public isNetRelevantFor(_clientId?: string, _clientData?: { position?: THREE.Vector3Like; [key: string]: any }): boolean {
    // If not replicated, this actor is never relevant
    if (!this._replicated) {
      return false;
    }

    // If onlyRelevantToOwner is true, only replicate to the owning client
    if (this._onlyRelevantToOwner) {
      return this._netOwningClientId === _clientId;
    }

    // If replicated, it's relevant by default
    // Subclasses can override to add per-client filtering (e.g., distance checks)
    // Example: return this.getWorldPosition().distanceTo(_clientData.position) < this.netRelevanceDistance;
    return true;
  }

  @property({category: 'Multiplayer'})
  public get replicated(): boolean {
    return this._replicated;
  }

  public set replicated(value: boolean) {
    if (this._netLocalRole !== NetRole.Authority) {
      console.warn(`[Actor] Not allowed to modify replicated flag on ${this.name}.`);
      return;
    }

    this._replicated = value;
    this._netRemoteRole = value ? NetRole.SimulatedProxy : NetRole.None;
  }

  @property({ replicate: true, category: 'Multiplayer'})
  public get netLocalRole(): NetRole {
    return this._netLocalRole;
  }

  public set netLocalRole(value: NetRole) {
    this._netLocalRole = value;
  }

  @property({ hidden: true })
  public get netOwningClientId(): string | null {
    return this._netOwningClientId;
  }

  public set netOwningClientId(value: string | null) {
    this._netOwningClientId = value;
  }

  @property({category: 'Multiplayer'})
  public get onlyRelevantToOwner(): boolean {
    return this._onlyRelevantToOwner;
  }

  public set onlyRelevantToOwner(value: boolean) {
    this._onlyRelevantToOwner = value;
  }

  @property({ replicate: true, hidden: true, transient: true })
  public get netRemoteRole(): NetRole {
    return this._netRemoteRole;
  }

  public set netRemoteRole(value: NetRole) {
    this._netRemoteRole = value;
  }

  /**
   * Set the remote network role to AutonomousProxy for player-controlled actors.
   * Should be called after replicated = true.
   * @param autonomousProxy - Whether to set as AutonomousProxy
   */
  public setAutonomousProxy(autonomousProxy: boolean): void {
    if (!this._replicated) {
      console.warn('[Actor] Cannot set AutonomousProxy on non-replicated actor.');
      return;
    }
    this._netRemoteRole = autonomousProxy ? NetRole.AutonomousProxy : NetRole.SimulatedProxy;
  }

  /**
   * Returns the ordered list of replicated components for this actor.
   *
   * Components are sorted by `(constructor.name, per-class ordinal)` — a stable,
   * deterministic order derived from depth-first traversal of the component tree.
   * Each component's {@link SceneComponent.replicationKey} is assigned here on first
   * call in the form `"ClassName:ordinal"` (e.g. `"MovementComponent:0"`).
   *
   * The sort order determines binary subId assignments used by the replication
   * protocol, so it must be identical on server and client for the same actor class.
   *
   * @throws If two replicated components share the same `replicationKey` (which would
   *   indicate non-deterministic component attachment order for that class).
   */
  public getReplicatedComponentMap(): SceneComponent[] {
    if (this._replicatedComponentMap) return this._replicatedComponentMap;

    // Collect in depth-first traversal order (deterministic for a given actor class).
    const allReplicated = this.getComponents(SceneComponent)
      .filter(c => c.isReplicated());

    // Assign per-class ordinals based on traversal order and build replicationKey.
    const componentOrdinals = new Map<SceneComponent, number>();
    const classOrdinals = new Map<Function, number>();
    for (const component of allReplicated) {
      const ctor = component.constructor;
      const ordinal = classOrdinals.get(ctor) ?? 0;
      classOrdinals.set(ctor, ordinal + 1);
      componentOrdinals.set(component, ordinal);
      component.replicationKey = `${ctor.name}:${ordinal}`;
    }

    // Sort by (className, ordinal) — class names alphabetically, then ordinal numerically.
    allReplicated.sort((a, b) => {
      const aName = a.constructor.name;
      const bName = b.constructor.name;
      if (aName !== bName) return aName < bName ? -1 : 1;
      return (componentOrdinals.get(a) ?? 0) - (componentOrdinals.get(b) ?? 0);
    });

    // Validate uniqueness — should be guaranteed by the ordinal scheme, but catch bugs early.
    const keys = new Set<string>();
    for (const component of allReplicated) {
      if (keys.has(component.replicationKey)) {
        throw new Error(
          `[Actor] Duplicate replication key "${component.replicationKey}" on actor "${this.name}". ` +
          'Replicated components of the same class must be added in a consistent order.'
        );
      }
      keys.add(component.replicationKey);
    }

    this._replicatedComponentMap = allReplicated;
    return allReplicated;
  }

  public hasAuthority(): boolean {
    return this._netLocalRole === NetRole.Authority;
  }

  public isLocalAutonomousProxy(): boolean {
    return this._netLocalRole === NetRole.AutonomousProxy;
  }

  public isSimulatedProxy(): boolean {
    return this._netLocalRole === NetRole.SimulatedProxy;
  }

  public isOwnedByLocalClient(): boolean {
    if (!this._netOwningClientId) {
      return false;
    }
    const netWorld = this.world?.netWorld;
    if (!netWorld) {
      return false;
    }
    return this._netOwningClientId === netWorld.clientId;
  }

  /**
   * Check if a method is a valid RPC target
   * @param methodName - The name of the method to validate
   * @returns True if the method is a valid RPC target
   */
  public isValidRPCMethod(methodName: string): boolean {
    const metadata: RPCMethodMetadata[] = Reflect.getMetadata(RPC_METADATA_KEY, this.constructor) ?? [];
    return metadata.some(m => m.methodName === methodName);
  }

  public getNetObjectId(): string {
    return `${ClassRegistry.getClassOrConstructorName(this)}'${this.uuid}'`;
  }

  /**
   * Check if this actor is a static object (Tier 1 or 2) that uses deterministic NetIDs.
   * Static objects are those loaded from world files.
   * @returns True if this is a static object
   */
  public isStaticNetObject(): boolean {
    return !this.remoteNetClientId;
  }

  public setRemoteNetClientId(clientId: string): void {
    this.remoteNetClientId = clientId;
  }

  public setRemoteNetDeviceId(deviceId: string): void {
    this.remoteNetDeviceId = deviceId;
  }

  public getNetDeviceId(): string {
    return this.remoteNetDeviceId ?? getDeviceId();
  }

  public getNetClientId(): string {
    if (this.remoteNetClientId) {
      return this.remoteNetClientId;
    }
    const netWorld = this.getWorld()?.netWorld;
    if (!netWorld) {
      throw new Error('Failed to get net world when getting net client id');
    }
    return netWorld.clientId;
  }

  /**
   * Get construction data for network spawning. Returns the serialized actor data needed to
   * reconstruct this actor on a client. Includes class info, init options, and non-replicated
   * properties. Replicated state is excluded here and sent separately via `initialState`.
   * @returns SerializedData for reconstructing the actor, or null if actor cannot be spawned over network
   */
  public getNetConstructionData(): SerializedData | null {
    return createDumper({ flags: DumperFlags.SkipReplicated }).dump(this);
  }

  public setOverrideMaterial(material: THREE.Material | null): this {
    this.rootComponent.setOverrideMaterial(material, true);
    return this;
  }

  //====================================================================================================

  protected override doBeginPlay(): void {
    this.rootComponent.beginPlay();
    this.onBeginPlay.invoke(this);
    this.getWorld()?.netWorld.addNetObject(this);
    if (this.isHiddenInGame()) {
      this.getWorld()?.setActorHiddenInGame(this, true);
    }
  }

  protected override doEndPlay(): void {
    if (this.isHiddenInGame()) {
      this.getWorld()?.setActorHiddenInGame(this, false);
    }
    this.getWorld()?.netWorld.removeNetObject(this);
    this.rootComponent.endPlay();
    this.onEndPlay.invoke(this);
    this.onEndPlay.clear();
    this.onBeginPlay.clear();
    this.onClicked.clear();
    this.onCollideWith.clear();
    this.onOverlapWith.clear();
    this.onStopCollidingWith.clear();
    this.onStopOverlappingWith.clear();
    this.world = null;
  }

  // calc all scene components for bounding boxes first
  public calcBoundingBox(): THREE.Box3 {
    this.rootComponent.updateMatrixWorld(true);
    const sceneComponents = this.getComponents(SceneComponent);
    for (const component of sceneComponents) {
      component.calcBoundingBox();
    }

    return this.getBoundingBox();
  }

  // simply use the cached bounding box of scene components
  public getBoundingBox(): THREE.Box3 {
    const box = new THREE.Box3();
    const sceneComponents = this.getComponents(SceneComponent);
    for (const component of sceneComponents) {
      const componentBox = component.boundingBox;
      if (componentBox) {
        box.union(componentBox);
      }
    }
    return box;
  }

  /**
   * Returns a description of this actor. Useful for AI assistants.
   * @returns - An object describing the actor's properties
   */
  public describe(options?: ActorDescriptionOptions): any {
    if (options?.omitDescription) {
      const simpleDescription: any = {
        name: this.name,
        uuid: this.uuid
      };

      return simpleDescription;
    }

    const description: any = {
      name: this.name,
      uuid: this.uuid,
      className: ClassRegistry.getClassName(this) ?? this.constructor.name,
      position: DescriptionHelper.dumpPosition(this.getWorldPosition()),
      rotation: DescriptionHelper.dumpRotation(this.getWorldRotation()),
      scale: DescriptionHelper.dumpScale(this.getWorldScale())
    };

    if (this.editorData?.description) {
      description.description = this.editorData.description;
    }

    this.calcBoundingBox();
    const boundingBox = this.getBoundingBox();
    if (!boundingBox.isEmpty()) {
      description.boundingBox = DescriptionHelper.dumpBoundingBox(this.getBoundingBox());
    }

    const describeSceneComponent = (component: THREE.Object3D) => {
      const componentDescription = component.describe({ recalculateBounds: false, includeDetails: options?.includeComponentsDetails });
      if (component.children && component.children.length > 0) {
        const children = component.children.map(describeSceneComponent).filter(child => child && Object.keys(child).length > 0);
        if (children.length > 0) {
          componentDescription.children = children;
        }
      }
      return componentDescription;
    };
    description.rootComponent = describeSceneComponent(this.rootComponent);

    return description;
  }

  public isHiddenInGame(): boolean {
    return this.hiddenInGame;
  }
  public setHiddenInGame(hidden: boolean) {
    this.hiddenInGame = hidden;
    if (this.world) {
      this.world.setActorHiddenInGame(this, hidden);
    }
  }

  public isEditorOnly(): boolean {
    return this.editorOnly;
  }

  public setEditorOnly(editorOnly: boolean) {
    this.editorOnly = editorOnly;
  }

  public getCamera(): THREE.Camera | null {
    const cameras = this.getComponents(THREE.Camera);
    return cameras.find(camera => camera.enabled) ?? null;
  }

  public getPlayerController(): PlayerController | null {
    return null;
  }

  public isPlayerControlled(): boolean {
    return !!this.getPlayerController();
  }

  public async waitForComponentsToLoad(): Promise<void> {
    const components = this.getComponents(SceneComponent);
    const asyncLoadables = components.filter(c => isAsyncLoadable(c)) as IAsyncLoadable[];
    const loadPromises = asyncLoadables.map(c => c.waitForLoad());
    await Promise.all(loadPromises);
  }

  public serialize(dumper: IDumper): void {
    if (this.rootComponent.isTransient()) {
      console.warn(
        `⚠️ [Serialization] Actor "${this.getPathName()}" has a transient root component; its transform will not be saved.`
      );
    }
    dumper.dumpValue('rootComponent', this.rootComponent, '_rootComponent');
    dumper.dumpProperties(this);
    if (this.isEditorOnly()) {
      dumper.markEditorOnly();
    }
  }

  public deserialize(loader: ILoader): void {
    // TODO: check if root component has changed in code
    const rootComponent = loader.loadValue('rootComponent', this.rootComponent) as SceneComponent;
    if (rootComponent && rootComponent !== this.rootComponent) {
      this.setRootComponent(rootComponent, false);
    }
    loader.loadProperties(this);

    // Migration shim: move legacy editorData.displayName → name
    if (this.editorData?.displayName) {
      this.name = this.editorData.displayName as string;
      delete this.editorData.displayName;
    }
  }

  //====================================================================================================
  // Editor Functions
  //====================================================================================================

  public onEditorPropertyChanged(path: string, value: any, result: EditorPropertyChangedResult): void {
    if (path === 'actorTags') {
      this.setActorTags(value as string[]);
    }
  }

  public onEditorSelectedChanged(isSelected: boolean, selectedActors: Actor[]): void {
    this.getComponents(SceneComponent).forEach(sceneComponent => sceneComponent.onEditorSelectedChanged(isSelected, selectedActors));
  }

  public onEditorComponentSelectionChanged(selectedComponent: SceneComponent | null, selectedActors: Actor[]): void {
    this.getComponents(SceneComponent).forEach(sceneComponent => sceneComponent.onEditorComponentSelectionChanged(selectedComponent, selectedActors));
  }

  public onEditorAddToWorld(): void {
    this.getComponents(SceneComponent).forEach(sceneComponent => sceneComponent.onEditorAddToWorld());
  }

  public onEditorRemoveFromWorld(): void {
    this.getComponents(SceneComponent).forEach(sceneComponent => sceneComponent.onEditorRemoveFromWorld());
  }

  public getConfigurationWarnings(): string[] {
    const warnings: string[] = [];
    // Add any actor-specific warnings here
    return warnings;
  }

  public getEditorClassIcon(): string | null {
    return 'Icon_Actor';
  }

  /**
   * Internal: Sets the world this actor belongs to
   * External code should never call this directly.
   * Use world.addActor(...) / world.removeActor(...) instead.
   * @param world - The world to set, or null to remove from current world
   */
  public _privateSetWorld(world: World | null): void {
    if (!ensure(!this.world || !world)) {
      return;
    }
    this.world = world;
    if (world) {
      this.onAddedToWorld.invoke(this, world);
    }
  }

  public get isEditor(): boolean {
    return this.getWorld()?.isEditorWorld ?? false;
  }
}
